import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/helpers/cn";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import InfoIcon from "@/assets/images/icons2/icon--information.svg?component";
import { useValidateFile } from "@/lib/hooks/useValidateFile";
import type { UUID } from "@/core/types";

import { ALLOWED_MIME, MAX_FILE_SIZE } from "./constants";
import { computeUnifiedStatus } from "./helpers";
import type { PhotoIDUploadStatus } from "./types";
import { PhotoPreview } from "./components/PhotoPreview";
import { PhotoUploader } from "./components/PhotoUploader";
import { UploadStatus } from "./components/UploadStatus";
import { useUploadPhotoID } from "./api/mutations/useUploadPhotoID";

export type PhotoIDUploadProps = {
  onParentStatusChange?: (status: PhotoIDUploadStatus) => void;
  patientId: UUID;
};

export function PhotoIDUpload({ onParentStatusChange, patientId }: PhotoIDUploadProps) {
  const [fileArray, setFileArray] = useState<Nullable<File[]>>(null);
  const uploadedFileRef = useRef<File | null>(null);

  const {
    fileName,
    isValid,
    mimeType,
    previewUrl,
    status: validationStatus,
    validatedFile,
  } = useValidateFile({
    acceptedTypes: ALLOWED_MIME,
    file: fileArray?.[0],
    maxSizeBytes: MAX_FILE_SIZE,
  });

  const { mutate: uploadImage, reset, status: uploadMutationStatus } = useUploadPhotoID();

  // Compute unified status from validation + upload states
  const unifiedStatus = computeUnifiedStatus({
    hasFile: !!fileArray?.[0],
    isValid,
    uploadMutationStatus,
    validationStatus,
  });

  // Combine validation + S3 upload states for loading indicator
  const isLoading = unifiedStatus === "validating" || unifiedStatus === "uploading";

  // Auto-upload when valid file is selected
  useEffect(() => {
    const file = fileArray?.[0];

    // No file selected - reset tracking
    if (!file) {
      uploadedFileRef.current = null;

      return;
    }

    // Already uploaded this exact file instance - prevent double upload
    if (uploadedFileRef.current === file) return;

    reset(); // reset mutation for new file

    // Upload if valid
    if (isValid && mimeType && validatedFile) {
      uploadedFileRef.current = file;
      uploadImage({ file: validatedFile, mimeType, patientId });
    }
  }, [fileArray, isValid, mimeType, patientId, reset, uploadImage, validatedFile]);

  // Notify parent of status changes
  useEffect(() => {
    onParentStatusChange?.(unifiedStatus);
  }, [onParentStatusChange, unifiedStatus]);

  return (
    <section
      className={cn(
        "tw:flex tw:flex-col tw:justify-center tw:gap-5 tw:p-4",
        "tw:rounded-lg tw:border tw:border-stroke-quaternary",
      )}
      data-testid="uploadSection"
      {...getAutomationDataAttr("photo-id-upload-section")}
    >
      <PhotoUploader
        fileName={fileName}
        fileStateSetter={setFileArray}
        hasFile={!!fileArray?.[0]}
        isLoading={isLoading}
        isValid={isValid}
      />

      {unifiedStatus === "success" && (
        <PhotoPreview fileName={fileArray?.[0]?.name} previewUrl={previewUrl} />
      )}

      <UploadStatus status={unifiedStatus} />
      <Divider2 className="tw:-mx-4" />

      <p className="body-sm tw:flex tw:items-center tw:gap-4">
        <InfoIcon className="tw:shrink-0 tw:text-fill-secondary" />
        Make sure your picture is not blurry and it clearly shows your face. Plain background
        recommended.
      </p>
    </section>
  );
}
